import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowLeft, ArrowRight } from 'lucide-react';

import { useLanguage } from 'shared/context/LanguageContext';
import { EQUIPMENT_STORE_ENABLED } from 'shared/lib/featureFlags';

interface TrainingResultProps {
  onContinueToStore?: () => void;
}

/**
 * Neutral end screen shown after the courier finishes the flow. It never
 * reveals the score or pass/fail - it only confirms completion and tells the
 * courier the team will contact them soon.
 */
const TrainingResult: React.FC<TrainingResultProps> = ({
  onContinueToStore,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const ForwardArrow = dir === 'rtl' ? ArrowLeft : ArrowRight;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mx-auto max-w-xl rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm"
    >
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#bf0035]/[0.08]">
        <CheckCircle2 size={36} className="text-[#bf0035]" />
      </div>
      <h2 className="text-xl font-bold text-gray-900">
        {t('portal.training.result.title')}
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-gray-600">
        {t('portal.training.result.message')}
      </p>
      <p className="mt-2 text-sm font-semibold text-gray-800">
        {t('portal.training.result.contact')}
      </p>

      {EQUIPMENT_STORE_ENABLED && onContinueToStore && (
        <button
          type="button"
          onClick={onContinueToStore}
          className="btn-primary mx-auto mt-6 inline-flex items-center justify-center gap-2"
        >
          {t('portal.training.continueToStore')}
          <ForwardArrow size={16} />
        </button>
      )}
    </motion.div>
  );
};

export default TrainingResult;
